import { db } from '../config/firebase';
import { 
  collection, 
  addDoc, 
  getDocs, 
  doc,
  getDoc,
  updateDoc,
  deleteDoc,
  query, 
  orderBy, 
  Timestamp 
} from 'firebase/firestore';
import type { QuestionnaireTemplate } from '../types/questionnaires';

export const templateService = {
  /**
   * Create a new questionnaire template
   * @param data Template data without id, createdAt, updatedAt
   * @returns Template ID
   */
  async createTemplate(data: Omit<QuestionnaireTemplate, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
    const docRef = await addDoc(collection(db, 'questionnaire_templates'), {
      ...data,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    });
    
    console.log('✅ Template created:', docRef.id, data.name);
    return docRef.id;
  },

  /**
   * List all questionnaire templates
   * @returns Array of templates sorted by name
   */
  async listTemplates(): Promise<QuestionnaireTemplate[]> {
    const q = query(
      collection(db, 'questionnaire_templates'), 
      orderBy('name', 'asc')
    );
    const snapshot = await getDocs(q);
    
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate() || new Date(),
      updatedAt: doc.data().updatedAt?.toDate() || new Date()
    } as QuestionnaireTemplate));
  },

  /**
   * Get a specific template by ID
   * @param id Template ID
   * @returns Template or null if not found
   */
  async getTemplate(id: string): Promise<QuestionnaireTemplate | null> {
    const docRef = doc(db, 'questionnaire_templates', id);
    const docSnap = await getDoc(docRef);
    
    if (!docSnap.exists()) return null;
    
    const data = docSnap.data();
    return {
      id: docSnap.id,
      ...data,
      createdAt: data.createdAt?.toDate() || new Date(),
      updatedAt: data.updatedAt?.toDate() || new Date()
    } as QuestionnaireTemplate;
  },

  /**
   * Update an existing template
   * @param id Template ID
   * @param updates Partial template data to update
   */
  async updateTemplate(id: string, updates: Partial<QuestionnaireTemplate>): Promise<void> {
    await updateDoc(doc(db, 'questionnaire_templates', id), {
      ...updates,
      updatedAt: Timestamp.now()
    });
    console.log('✅ Template updated:', id);
  },

  /**
   * Delete a template (only if not system template and not in use)
   * @param id Template ID
   */
  async deleteTemplate(id: string): Promise<void> {
    // Check if template is system template
    const template = await this.getTemplate(id);
    if (template?.isSystem) {
      throw new Error('System-Vorlagen können nicht gelöscht werden');
    }
    
    // TODO: Add check if template is in use by any questionnaires
    
    await deleteDoc(doc(db, 'questionnaire_templates', id));
    console.log('✅ Template deleted:', id);
  }
};

